<!-- AdviceSection.vue -->
<template>
  <div class="advice-section" ref="adviceSection" :data-ai-generated="hasNotes ? 'true' : null">
    <!-- Section title -->
    <div class="section-title">
      <h2 v-underline-primary>{{ h2 }}</h2>
      <p>{{ subtitle }}</p>
      <div v-if="model_used || iteration_used" class="model-iteration"> {{ $t('explore.advice.beta', { iteration: iteration_used, model: model_used }) }}</div>
    </div>
    <p v-if="hasNotes" class="ai-disclaimer"><i class="fas fa-robot" aria-hidden="true"></i> {{ $t('explore.advice.aiDisclaimer') }}</p>
    <!-- Display sticky notes if there are any results -->
    <div v-if="localResult && localResult.length > 0" class="sticky-notes" :class="{ list: hasVideos }">
      <div
        v-for="(r, i) in localResult"
        :key="i"
        role="button"
        tabindex="0"
        :aria-pressed="selectedStickyNote === i ? 'true' : 'false'"
        @click="selectStickyNote(i)"
        @keydown.enter.prevent="selectStickyNote(i)"
        @keydown.space.prevent="selectStickyNote(i)"
        :class="[r.video_id ? 'video-card' : 'sticky-note', { selected: selectedStickyNote === i }]"
        :data-ai-generated="r.video_id ? null : 'true'"
      >
        <!-- <span v-if="!r.video_id" class="ai-badge"><i class="fas fa-robot" aria-hidden="true"></i> {{ $t('explore.advice.aiBadge') }}</span> -->
        <button v-if="dismissable && !r.video_id" type="button" class="dismiss" :aria-label="$t('explore.advice.dismiss')" :title="$t('explore.advice.dismiss')" @click.stop="dismiss(i)" @keydown.enter.stop @keydown.space.stop>
          <i class="fas fa-xmark" aria-hidden="true"></i>
        </button>
        <p v-if="r.category" class="category">{{ r.category }} {{ getCategoryEmoji(r.category) }}</p>

        <div v-if="r.video_id" class="video-main">
          <span class="video-thumb" aria-hidden="true">
            <img v-if="r.thumbnail && r.thumbnail !== 'null'" :src="r.thumbnail" alt="" loading="lazy" />
            <i v-else class="fab fa-youtube"></i>
          </span>
          <div class="video-info">
            <p class="title">{{ r.title }}</p>
            <p class="meta">{{ [r.channel, r.duration_string].filter(Boolean).join(' · ') }}</p>
            <p class="meta">{{ $t('explore.advice.ratingSummary', { avg: r.rating_amount > 0 ? (r.rating / r.rating_amount).toFixed(1) : '-', n: r.rating_amount || 0 }, r.rating_amount || 0) }}</p>
          </div>
          <a :href="'https://www.youtube.com/watch?v=' + r.video_id" target="_blank" rel="noopener" class="play-btn" :aria-label="$t('explore.advice.watch')" @click.stop>
            <i class="fas fa-play" aria-hidden="true"></i>
          </a>
        </div>
        <template v-else>
          <template v-if="r.advice">
            <p class="note-text" :class="{ typing: r.typing }" :lang="noteLang(r)">{{ noteText(r) }}</p>
            <p v-if="noteText(r) !== r.advice" class="en-fallback">{{ $t('explore.advice.shownInEnglish') }}</p>
          </template>
          <p v-else-if="!r.title" class="note-text">{{ typeof r === 'string' ? r : '' }}</p>
          <div class="sticky-bottom">
            <StarRating
              v-model="localRatings[i]"
              :index="i"
              :uniqueID="`advice_${r.record_id}`"
              @input="updateRating(i, $event)"
            />
            <div class="rating-average">
              <span class="rating-symbol">&#248;</span>
              {{ $t('explore.advice.ratingSummary', { avg: r.rating_amount > 0 ? (r.rating / r.rating_amount).toFixed(1) : '-', n: r.rating_amount || 0 }, r.rating_amount || 0) }}
            </div>
          </div>
        </template>
      </div>
      <!-- 
      <button class="add-note" @click="addNote">
        <i class="fas fa-plus-circle"></i> Add Note
      </button> -->
    </div>
    <!-- Display a message if there are no results -->
    <div v-else class="no-results">
      <p>{{ $t('explore.advice.noAdvice') }}</p>
    </div>
  </div>
</template>

<script>
import StarRating from "./StarRating.vue";

export default {
  name: "AdviceSection",
  components: {
    StarRating,
  },
  props: {
    h2: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: true,
    },
    ratings: {
      type: Array,
      required: true,
      default: () => [],
    },
    result: {
      type: Array,
      required: true,
      default: () => [],
    },
    model_used: {
      type: String,
      default: null,
    },
    iteration_used: {
      type: String,
      default: null,
    },
    // Show an X on every AI note so people can remove suggestions they do not want
    dismissable: {
      type: Boolean,
      default: false,
    },

  },

  data() {
    return {
      selectedStickyNote: null,
      localRatings: this.ratings.slice(),
      localResult: this.result.result
        ? this.result.result.slice()
        : this.result.slice(),
    };
  },
  computed: {
    hasNotes() {
      return this.localResult.some((r) => !r.video_id);
    },
    hasVideos() {
      return this.localResult.some((r) => r.video_id);
    },
    averageRating() {
      const totalRatings = this.localResult.reduce(
        (acc, curr) => acc + curr.rating,
        0
      );
      const totalRatingsAmount = this.localResult.reduce(
        (acc, curr) => acc + curr.rating_amount,
        0
      );
      return totalRatingsAmount > 0
        ? (totalRatings / totalRatingsAmount).toFixed(1)
        : "-";
    },
  },

  mounted() {},
  watch: {
    result: {
      immediate: true, // trigger the watcher immediately when the component is created
      handler(newResult) {
        // While ideas are still being written just show them; records are loaded once streaming has finished
        if (newResult.some((item) => item.streaming)) {
          this.localResult = newResult;
          return;
        }
        if (newResult.some((item) => item.video_id)) {
          this.fetchVideoDetails();
        }
        if (newResult.some((item) => item.record_id)) {
          this.fetchAdviceDetails();
        } else {
          this.localResult = newResult;
        }
      },
    },
  },

  methods: {
    // Method to select a sticky note and emit the select-sticky-note event
    selectStickyNote(index) {
      this.selectedStickyNote = index;
      this.$emit("select-sticky-note", { index, rating: this.ratings[index] });
    },

    getCategoryEmoji(category) {
      const emojis = {
        "budget friendly": "💰",
        "fast to achieve": "🚀",
        "best practice": "🏆",
        "why it works": "💚",
        "spread it": "🌍",
        "super creative": "🎨",
        "citizen action": "🕺",
        "municipality action": "🏛️",
      };
      return emojis[category.toLowerCase()] || "";
    },
    async fetchVideoDetails() {
      const videoIds = this.result
        .filter((item) => item.video_id)
        .map((item) => item.video_id);

      for (const videoId of videoIds) {
        let record;
        try {
          // requestKey: null = never auto-cancel. Several AdviceSections can load at once.
          record = await this.$pb
            .collection("video_db")
            .getFirstListItem(`video_id="${videoId}"`, { requestKey: null });
        } catch (error) {
          console.warn(`Video ${videoId} could not be loaded`, error);
          continue;
        }
        // console.log('record', record);
        const index = this.result.findIndex(
          (item) => item.video_id === videoId
        );
        if (index >= 0 && record) {
          // Merge the fetched record properties with the existing result object
          this.localResult[index] = { ...this.result[index], ...record };
          this.$emit("update:result", this.localResult);
        }
      }
    },
    async fetchAdviceDetails() {
      const recordIds = this.result
        .filter((item) => item.record_id)
        .map((item) => item.record_id);
      for (const recordId of recordIds) {
        try {
          const record = await this.$pb
            .collection("infrastructure_recs")
            .getOne(recordId, { requestKey: null });
          const index = this.result.findIndex(
            (item) => item.record_id === recordId
          );
          if (index >= 0 && record) {
            // Merge the fetched record properties with the existing result object
            this.localResult[index] = { ...this.result[index], ...record };
            this.$emit("update:result", this.localResult);
          }
        } catch (error) {
          console.error(
            `Error fetching advice details for record ${recordId}:`,
            error
          );
        }
      }
    },
    // Notes are written in the reporter's language and always also in English. Show the viewer's language when we have it, English otherwise.
    noteText(r) {
      if (r.advice_en && r.advice_lang && r.advice_lang !== this.$i18n.locale) return r.advice_en;
      return r.advice;
    },
    noteLang(r) {
      return this.noteText(r) === r.advice ? r.advice_lang || undefined : "en";
    },
    dismiss(index) {
      this.localResult.splice(index, 1);
      this.localRatings.splice(index, 1);
      this.selectedStickyNote = null;
      this.$emit("update:result", this.localResult.slice());
      this.$emit("update:ratings", this.localRatings);
    },
    // Method to update the rating for a sticky note
    updateRating(index, value) {
      this.localRatings[index] = value;
      this.$emit("update:ratings", this.localRatings);

      const currentItem = this.localResult[index];

      // Update rating and rating amount in video_db record if it exists
      const videoId = currentItem.id;
      if (videoId) {
        this.$pb
          .collection("video_db")
          .update(videoId, {
            rating: (currentItem.rating || 0) + value,
            rating_amount: (currentItem.rating_amount || 0) + 1,
          })
          .catch((error) => {
            console.error(
              `Error updating rating in video_db record for video ${videoId}:`,
              error
            );
          });
      }
      const recordId = currentItem.record_id;
      // Update rating and rating amount in infrastructure_recs record if it exists
      if (recordId) {
        this.$pb
          .collection("infrastructure_recs")
          .update(recordId, {
            rating: (currentItem.rating || 0) + value,
            rating_amount: (currentItem.rating_amount || 0) + 1,
          })
          .catch((error) => {
            console.error(
              `Error updating rating in infrastructure_recs record for record ${recordId}:`,
              error
            );
          });
      }
    },
  },
};
</script>

<style scoped>
/* Cards stack full width (B2). Notes are sticky-note yellow; videos are surface cards with a small preview. */
.sticky-notes { display: flex; gap: 12px; padding: 8px 0; text-align: start; overflow-x: auto; overflow-y: hidden; overscroll-behavior-x: contain; scroll-snap-type: x mandatory; scrollbar-width: thin; }
.sticky-notes > .sticky-note { flex: none; width: clamp(220px, 40%, 300px); scroll-snap-align: start; }
.sticky-notes.list { flex-direction: column; overflow: visible; }

.sticky-note,
.video-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: var(--radius-lg);
  overflow-wrap: anywhere;
  text-align: start;
  animation: fade-in 0.5s ease-out both;
}
.sticky-note { position: relative; background: var(--color-stickynote); color: var(--color-on-stickynote); }
.video-card { background: var(--color-surface); color: var(--color-text); border: 1px solid var(--color-border); padding: 12px; }
.sticky-note.selected,
.video-card.selected { outline: 2px solid var(--color-primary-text); outline-offset: 2px; }

.sticky-note p, .video-card p { margin: 0; }
.category { font-size: 12px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-secondary); }
.dismiss { position: absolute; top: 4px; right: 4px; display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border: 0; border-radius: 50%; background: transparent; color: var(--color-on-stickynote); cursor: pointer; }
.dismiss:hover { background: rgba(0, 0, 0, 0.08); }
.sticky-note:has(.dismiss) .category, .sticky-note:has(.dismiss) .note-text { padding-inline-end: 28px; }
.en-fallback { font-size: 12px; opacity: 0.75; }
.sticky-note .category { color: var(--color-on-stickynote); opacity: 0.8; }
.note-text { font-size: 15px; line-height: 1.5; }
.note-text.typing::after { content: ''; display: inline-block; width: 2px; height: 1em; margin-inline-start: 2px; vertical-align: text-bottom; background: currentColor; animation: caret-blink 1s steps(1) infinite; }
@keyframes caret-blink { 50% { opacity: 0; } }
@media (prefers-reduced-motion: reduce) { .note-text.typing::after { animation: none; } }

.video-main { display: flex; gap: 12px; align-items: flex-start; }
.video-main .video-thumb { margin-top: 2px; }
.video-thumb { flex: none; display: flex; align-items: center; justify-content: center; width: 112px; height: 70px; border-radius: 8px; overflow: hidden; background: var(--color-subtle); color: var(--color-secondary); }
.video-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.video-info { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.video-info .title { font-size: 14px; font-weight: 600; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
.video-info .meta { font-size: 12px; color: var(--color-secondary); }
.video-info { flex: 1; }
.play-btn { flex: none; align-self: center; display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; background: var(--color-primary); color: var(--color-on-primary); text-decoration: none; }
.play-btn:hover { background: var(--color-primary-dark); }

.ai-disclaimer { margin: 0 0 8px; font-size: 13px; line-height: 1.4; color: var(--color-secondary); text-align: start; }
.ai-badge { align-self: flex-start; display: inline-flex; align-items: center; gap: 6px; padding: 2px 10px; border-radius: var(--radius-pill); background: var(--color-on-stickynote); color: var(--color-stickynote); font-size: 12px; font-weight: 600; }
.sticky-bottom { margin-top: auto; display: flex; flex-direction: column; gap: 4px; align-items: flex-start; }
.rating-average { font-size: 12px; }

.youtube-link {
  display: inline-flex; align-items: center; gap: 8px; min-height: var(--touch-min); padding: 0 14px;
  border: 2px solid var(--color-border-input); border-radius: var(--radius-pill);
  color: var(--color-text); font-size: 14px; font-weight: 600; text-decoration: none;
}
.youtube-link:hover { border-color: var(--color-primary-text); color: var(--color-primary-text); }

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
  }
}

</style>
